import { useEffect, useState } from "react";
import { Heading, Subheading } from "../../design/heading";
import { Key } from "../../models/Key";
import { useAuth } from "../../contexts/AuthContext";
import { Field, FieldGroup, Fieldset, Label } from "../../design/fieldset";
import { Divider } from "../../design/divider";
import { Input } from "../../design/input";
import { Button } from "../../design/button";
import SelectArtist from "../../components/SelectArtist";
import { Select } from "../../design/select";
import axios from "axios";
import { AuctionRecord } from "../../models/AuctionRecord";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import moment from "moment";
import { Link } from "../../design/link";
import PricePointChart from "./components/PricePointChart";
import { PricePoint } from "../../models/PricePoint";
import { priceString } from "../../utils/string";

interface APIResponse {
  summary?: string;
  priceGrowth: PricePoint[];
  auctionRecords: AuctionRecord[];
  price: number;
}

const DescriptionItem = ({
  label,
  value,
  href,
}: {
  label: string;
  value: string | number | null;
  href?: string;
}) => {
  if (!value) {
    return null;
  }
  return (
    <>
      <DescriptionTerm>{label}</DescriptionTerm>
      <DescriptionDetails>
        {href && <Link href={href}>{value}</Link>}
        {!href && value}
      </DescriptionDetails>
    </>
  );
};

const DescriptionPage: React.FC = () => {
  const { user } = useAuth();

  const [result, setResult] = useState<APIResponse | null>(null);
  const [artist, setArtist] = useState<any>(null);
  const [category, setCategory] = useState<string>("painting");
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [description, setDescription] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!artist) {
      alert("Please select an artist");
      return;
    }

    if (!category) {
      alert("Please select a category");
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await user?.getIdToken();
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/describe`,
        {
          artist: artist.objectID,
          category,
          description: description ?? "",
          width,
          height,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      console.log(data);
      setResult(data.data);
    } catch (error) {
      console.error(error);
    }
    setIsSubmitting(false);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div>
          <Heading>Valuations</Heading>
        </div>
      </div>
      {!result && (
        <div className="flex items-center justify-center min-h-screen">
          <form
            method="post"
            className="w-full max-w-lg p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900"
            onSubmit={handleSubmit}
          >
            <Fieldset>
              <Heading className="text-2xl font-semibold text-center">
                Valuation
              </Heading>
              <Divider className="my-6" />
              <FieldGroup>
                <Field>
                  <Label htmlFor="artist">Artist</Label>
                  <div>
                    <SelectArtist artist={artist} onSelectArtist={setArtist} />
                  </div>
                </Field>
                <Field>
                  <Label htmlFor="category">Category</Label>
                  <Select
                    id="category"
                    value={category || ""}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="painting">Painting</option>
                    <option value="sculpture">Sculpture</option>
                    <option value="drawing">Drawing</option>
                    <option value="print">Print</option>
                    <option value="photograph">Photograph</option>
                  </Select>
                </Field>

                <Field>
                  <Label htmlFor="width">Width</Label>
                  <Input
                    type="number"
                    value={width}
                    onChange={(e) => setWidth(parseInt(e.target.value))}
                    placeholder="Enter the width"
                  />
                </Field>
                <Field>
                  <Label htmlFor="height">Height</Label>
                  <Input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(parseInt(e.target.value))}
                    placeholder="Enter the height"
                  />
                </Field>

                <Field>
                  <Label htmlFor="width">Description</Label>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    id="description"
                    type="text"
                    placeholder="Enter a description of the artwork"
                  />
                </Field>
              </FieldGroup>
              <Divider className="my-6" />
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Fieldset>
          </form>
        </div>
      )}
      {result && result.price && (
        <>
          <Subheading>Your painting has the potential to be worth:</Subheading>
          <Heading>{priceString(result.price / 100)}</Heading>
        </>
      )}
      {result && result.priceGrowth && (
        <>
          <Subheading className="mt-8">Price Growth</Subheading>
          <PricePointChart pricePoints={result.priceGrowth} />
        </>
      )}
      {result && result.auctionRecords.length > 0 && (
        <>
          <Subheading className="mt-8">Auction Records</Subheading>
          <Table>
            <TableBody>
              {result.auctionRecords.map((record) => {
                return (
                  <TableRow key={record._id}>
                    <TableCell>
                      <img
                        src={record.medium_image_url}
                        alt={record.title}
                        style={{ width: 200 }}
                      />
                    </TableCell>
                    <TableCell>
                      <DescriptionList>
                        <DescriptionItem label="Id" value={record._id} />
                        <DescriptionItem
                          label="Artist"
                          value={record.artist?.name}
                        />
                        <DescriptionItem label="Title" value={record.title} />
                        <DescriptionItem label="Year" value={record.year} />
                        <DescriptionItem label="Medium" value={record.medium} />
                        <DescriptionItem
                          label="Category"
                          value={record.category}
                        />
                        <DescriptionItem
                          label="Dimensions"
                          value={record.size}
                        />
                        <DescriptionItem
                          label="Hammer Price"
                          value={
                            record.usd_hammer_price &&
                            priceString(record.usd_hammer_price / 100)
                          }
                        />
                        <DescriptionItem
                          label="Estimate"
                          value={
                            record.usd_estimate_low &&
                            record.usd_estimate_high &&
                            `${priceString(record.usd_estimate_low / 100)} - ${priceString(record.usd_estimate_high / 100)}`
                          }
                        />
                        <DescriptionItem
                          label="Sale Date"
                          value={
                            record.auction &&
                            moment(record.sale_date).format("MMMM DD, YYYY")
                          }
                          href={record.website_url}
                        />
                      </DescriptionList>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
    </>
  );
};
export default DescriptionPage;
